/** @format */

import { AnalyticsService, UtilityService } from '@shared/data-access/services';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  HomefieldIconsLibraryComponent,
  HomefieldIconsLibraryService,
  homefieldIconChevronLeftOutline,
  homefieldIconChevronRightOutline,
} from '@dsg-tech/homefield-angular-icons';
import {
  pageNumberChanged,
  selectPageNumber,
  selectPageSize,
  selectProductList,
  selectTotalCount,
} from '@plp/data-access/state';

import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { HomefieldTextLinkModule } from '@ecomm-patterns-components';
import { ProductListLayoutService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';

@Component({
  selector: 'plp-components-bottom-pagination',
  imports: [HomefieldIconsLibraryComponent, HomefieldTextLinkModule, HmfActionTextComponent],
  templateUrl: './bottom-pagination.component.html',
  styleUrl: './bottom-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomPaginationComponent {
  private readonly document = inject(DOCUMENT);
  private readonly utilityService = inject(UtilityService);
  private readonly store = inject(Store);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
  private readonly productListLayoutService = inject(ProductListLayoutService);

  readonly pageNumber = this.store.selectSignal(selectPageNumber);
  readonly productListData = this.store.selectSignal(selectProductList);
  readonly pageSize = this.store.selectSignal(selectPageSize);
  readonly totalCount = this.store.selectSignal(selectTotalCount);

  readonly isColorBreakoutPage = input(false);

  selectedIndex = 0;

  readonly totalPages = computed(() => {
    const pageSize = this.pageSize();
    const totalCount = this.totalCount();

    return pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;
  });

  readonly displayPages = computed(() =>
    this.calculateDisplayPages(this.pageNumber(), this.totalPages()),
  );

  // Show right ellipsis if the last page is not in the displayed pages
  readonly showRightEllipsis = computed(
    () => this.displayPages() && !this.displayPages().includes(this.totalPages()),
  );

  // Show left ellipsis if the first or second page is not in the displayed pages
  readonly showLeftEllipsis = computed(
    () =>
      this.displayPages() &&
      this.pageNumber() !== 0 &&
      ![1, 2].some((val) => this.displayPages().includes(val)),
  );

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconChevronLeftOutline,
      homefieldIconChevronRightOutline,
    ]);
  }

  async pageNumberCountClickEvent(pageNumber: number, index?: number): Promise<void> {
    this.selectedIndex = index ?? 0;

    const topOfBreakoutPage = this.document.getElementById('plp-list-container');

    this.productListLayoutService.scrollToTop({
      element: this.isColorBreakoutPage() && topOfBreakoutPage ? topOfBreakoutPage : undefined,
      scrollBehavior: 'instant',
    });

    await this.utilityService.yieldTask(() => {
      this.store.dispatch(pageNumberChanged({ pageNumber }));
      this.setAnalyticsEvent(pageNumber + 1);
    });
  }

  async lastNumberClick(totalPages: number) {
    const index = (totalPages % 4) - 1;
    await this.pageNumberCountClickEvent(totalPages - 1, index);
  }

  async decreasePageNumber() {
    // reset the selected index if arrow click is past the displayed page numbers
    const index = this.selectedIndex <= 0 ? 3 : this.selectedIndex - 1;
    await this.pageNumberCountClickEvent(this.pageNumber() - 1, index);
  }

  async increasePageNumber() {
    const index = this.selectedIndex >= 3 ? 0 : this.selectedIndex + 1;
    await this.pageNumberCountClickEvent(this.pageNumber() + 1, index);
  }

  /** @description update the page numbers shown in the displayed pagination */
  private calculateDisplayPages(currentPage: number, totalPages: number): number[] {
    currentPage += 1;

    // If there are less than 5 pages, then just display them all
    if (totalPages < 5) {
      return [...Array(totalPages).keys()].map((i) => i + 1);
    }

    // If we are on the 3rd page (special cases)
    if (currentPage === 3) {
      return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1];
    }

    // If we are on the 3rd page from the end (special case)
    if (currentPage === totalPages - 2) {
      return [currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
    }

    // If we are not on the end caps
    if (currentPage !== 1 && currentPage !== totalPages) {
      return [currentPage - 1, currentPage, currentPage + 1];
    }

    // If we are on the first page
    if (currentPage === 1) {
      return [currentPage, currentPage + 1, currentPage + 2];
    }

    // We must be on the last page
    return [currentPage - 2, currentPage - 1, currentPage];
  }

  private setAnalyticsEvent(pageNumber: number): void {
    const refinementUpdateObj = {
      UpdateType: 'Pagination',
      UpdateValue: pageNumber,
    };
    this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObj);
  }
}
